import type { Locale } from '@/i18n'
import type { Runtime } from '@/types'
import { formatTimestamp } from '@/utils/presentation'
import { runtimeDisplayName } from '@/utils/runtimeAlias'
import { runtimeHealthPresentation, runtimeMatchesFilter, runtimeRelativeTime, runtimeWebPresentation, sortRuntimeList, type RuntimeListFilter, type RuntimeListSort } from '@/utils/runtimeList'
export type RuntimeSearch = { q: string; filter: RuntimeListFilter; sort: RuntimeListSort }
export function validateRuntimeSearch(search: Record<string, unknown>): RuntimeSearch {
  return {
    q: typeof search.q === 'string' ? search.q.slice(0, 200) : '',
    filter: typeof search.filter === 'string' && ['all', 'online', 'attention', 'offline'].includes(search.filter) ? search.filter as RuntimeListFilter : 'all',
    sort: typeof search.sort === 'string' && ['recent', 'name', 'status'].includes(search.sort) ? search.sort as RuntimeListSort : 'recent',
  }
}
export function selectRuntimes(items: Runtime[], search: RuntimeSearch): Runtime[] {
  const q = search.q.trim().toLocaleLowerCase()
  return sortRuntimeList(items.filter(runtime => runtimeMatchesFilter(runtime, search.filter)
    && (!q || `${runtime.cloudDisplayName} ${runtime.deviceName ?? ''} ${runtime.id} ${runtime.version} ${runtime.region ?? ''}`.toLocaleLowerCase().includes(q))), search.sort)
}
export function mergeRuntimeSnapshot(current: Runtime, incoming: Runtime, fullSnapshot: boolean): Runtime {
  if (current.id !== incoming.id) return current
  if (incoming.ownershipEpoch < current.ownershipEpoch) return current
  if (incoming.ownershipEpoch !== current.ownershipEpoch) return fullSnapshot ? incoming : current
  const resourceOlder = current.resourceVersion !== undefined && incoming.resourceVersion !== undefined && incoming.resourceVersion < current.resourceVersion
  const aliasOlder = current.cloudDisplayNameVersion !== undefined && (incoming.cloudDisplayNameVersion === undefined || incoming.cloudDisplayNameVersion < current.cloudDisplayNameVersion)
  const base = fullSnapshot && !resourceOlder ? incoming : current
  const alias = aliasOlder ? current : incoming
  return { ...base, cloudDisplayName: runtimeDisplayName(base.id, alias.cloudDisplayNameOverride, base.deviceName), cloudDisplayNameOverride: alias.cloudDisplayNameOverride, cloudDisplayNameVersion: alias.cloudDisplayNameVersion }
}
export function observationTime(value: string | undefined, locale: Locale, now = Date.now()) {
  const parsed = value ? Date.parse(value) : Number.NaN
  const missing = locale === 'zh-CN' ? '未提供' : 'Not provided'
  if (!Number.isFinite(parsed)) return { absolute: missing, relative: missing }
  return {
    absolute: formatTimestamp(value, locale),
    relative: parsed > now + 5_000 ? (locale === 'zh-CN' ? '观测时间晚于当前时钟' : 'Observation is ahead of the current clock') : runtimeRelativeTime(value, locale, now),
  }
}
export function runtimeReadPresentation(runtime: Runtime, locale: Locale, verifiable: boolean) {
  if (!verifiable) return {
    health: { code: 'unverifiable' as const, label: locale === 'zh-CN' ? '状态不可验证' : 'Status unverifiable', tone: 'muted' as const },
    web: { code: 'unverifiable' as const, label: locale === 'zh-CN' ? '状态不可验证' : 'Status unverifiable', tone: 'muted' as const,
      reason: locale === 'zh-CN' ? '刷新失败，请重新读取并核对状态后再建立连接。' : 'Refresh failed. Reload and verify the state before connecting.' },
  }
  return { health: runtimeHealthPresentation(runtime, locale), web: runtimeWebPresentation(runtime, locale) }
}
